/**
 * Drill-in drawer for one customer on /customer-management: every headset
 * with its daily hours (up to the snapshot's history, default 56 days), and
 * that customer's weekly-email settings, preview and test send.
 */
import React, { useEffect, useMemo, useState } from 'react';

import Alert from './Alert';
import Button from './Button';
import DataTable, { DataTableColumn } from './DataTable';
import LoadingSpinner from './LoadingSpinner';
import {
  consoleApi, deviceSilent, E2Device, E2Org, E2Snapshot, EmailMode, EmailPreview, fmtDay, fmtH,
  fmtStamp, fmtWhen, OrgEmail, silentLabel, Status, STATUS_LABEL, statusOf, Suggestion,
} from './customerManagement';

interface Props {
  org: string;
  /** The customer's name as the wall and emails show it. */
  label?: string;
  snapshot: E2Snapshot;
  orgRow: (E2Org & { status: Status }) | null;
  devices: E2Device[];
  email: OrgEmail | null;
  globalEnabled: boolean;
  defaultOn: boolean;
  nextReportWeek: string;
  threshold: number;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}

interface HeadsetRow extends E2Device {
  daily: number[];
  silent: number | null;
  status: Status;
}

const RANGES = [7, 14, 28, 56];

function mondaysBack(fromIso: string, n: number): string[] {
  const [y, m, d] = fromIso.split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  return Array.from({ length: n }, (_, i) => {
    const x = new Date(base.getTime() - i * 7 * 86_400_000);
    return x.toISOString().slice(0, 10);
  });
}

const CustomerOrgDetail: React.FC<Props> = ({
  org, label, snapshot, orgRow, devices, email, globalEnabled, defaultOn, nextReportWeek, threshold,
  onClose, onSaved, onError,
}) => {
  const labels = useMemo(
    () => snapshot.history_labels || snapshot.hours.day_labels,
    [snapshot],
  );
  const maxRange = labels.length;
  const [range, setRange] = useState(Math.min(14, maxRange));
  const paceTarget = snapshot.target.pace_hours_per_day;
  const weekTarget = snapshot.target.week_hours_per_headset;

  const [mode, setMode] = useState<EmailMode>(email?.mode || 'inherit');
  const [recipientsText, setRecipientsText] = useState((email?.recipients || []).join(', '));
  const [displayName, setDisplayName] = useState(email?.display_name || org);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [previewWeek, setPreviewWeek] = useState(nextReportWeek);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [previewKind, setPreviewKind] = useState<'summary' | 'reminder'>('summary');
  const [testConfirm, setTestConfirm] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [localMsg, setLocalMsg] = useState<string | null>(null);

  useEffect(() => {
    setMode(email?.mode || 'inherit');
    setRecipientsText((email?.recipients || []).join(', '));
  }, [org, email]);

  useEffect(() => {
    let live = true;
    setSuggestions(null);
    consoleApi<{ suggestions: Suggestion[] }>(`/suggested-recipients?org=${encodeURIComponent(org)}`)
      .then((r) => { if (live) setSuggestions(r.suggestions || []); })
      .catch(() => { if (live) setSuggestions([]); });
    return () => { live = false; };
  }, [org]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (preview) setPreview(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, preview]);

  const shownLabels = labels.slice(-range);

  const rows: HeadsetRow[] = useMemo(() => devices.map((d) => {
    const hist = d.history && d.history.length === labels.length ? d.history : d.days;
    const silent = deviceSilent(d, snapshot.today);
    return {
      ...d,
      daily: hist.slice(-range),
      silent,
      status: statusOf(d.last_recording, silent, d.pace, paceTarget, threshold),
    };
  }).sort((a, b) => b.week_hours - a.week_hours || a.id.localeCompare(b.id)),
  [devices, labels, range, snapshot.today, paceTarget, threshold]);

  const recipients = recipientsText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  const dirty = mode !== (email?.mode || 'inherit')
    || recipients.join(',') !== (email?.recipients || []).join(',')
    || displayName.trim() !== (email?.display_name || org);

  const save = async () => {
    setSaving(true);
    try {
      await consoleApi(`/orgs/${encodeURIComponent(org)}`, {
        method: 'PUT', body: JSON.stringify({ mode, recipients, display_name: displayName.trim() }),
      });
      onSaved(`Email settings saved for ${org}.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const addSuggestion = (addr: string) => {
    if (recipients.some((r) => r.toLowerCase() === addr.toLowerCase())) return;
    setRecipientsText([...recipients, addr].join(', '));
  };

  const loadPreview = async (week: string) => {
    setPreviewLoading(true);
    setPreviewWeek(week);
    try {
      const p = await consoleApi<EmailPreview>(
        `/preview?org=${encodeURIComponent(org)}&kind=${previewKind}${week ? `&week=${week}` : ''}`,
      );
      setPreview(p);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    setTestSending(true);
    try {
      const r = await consoleApi<{ ok: boolean; to: string; sandbox: boolean }>('/send-test', {
        method: 'POST', body: JSON.stringify({ org, kind: previewKind, to: testTo.trim(), week: previewWeek || undefined }),
      });
      setLocalMsg(r.sandbox
        ? `Test sent in sandbox mode (redirected to the internal sandbox recipients, not ${r.to}).`
        : `Test email sent to ${r.to}.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setTestSending(false);
      setTestConfirm(false);
    }
  };

  const resolvedOn = mode === 'on' || (mode === 'inherit' && defaultOn);
  const weekOptions = nextReportWeek ? mondaysBack(nextReportWeek, 8) : [];
  const dense = range > 14;

  const columns: DataTableColumn<HeadsetRow>[] = [
    {
      key: 'id',
      header: 'Headset',
      render: (d) => (
        <div className="cm-headset-cell">
          <code className="cm-id">{d.id}</code>
          {!d.last_recording && <span className="cm-badge cm-badge-never">Never recorded</span>}
        </div>
      ),
    },
    {
      key: 'daily',
      header: (
        <div className={`cm-days-head ${dense ? 'cm-dense' : ''}`}>
          {shownLabels.map((l, i) => (
            <span key={l} title={fmtDay(l)}>
              {dense ? (i % 7 === 0 ? l.slice(5) : '') : fmtDay(l).split(',')[0]}
            </span>
          ))}
        </div>
      ),
      render: (d) => (
        <div className={`cm-days ${dense ? 'cm-dense' : ''}`}>
          {d.daily.map((v, i) => {
            const lvl = v <= 0 ? 0 : v >= paceTarget ? 3 : v >= paceTarget / 2 ? 2 : 1;
            return (
              <span
                key={shownLabels[i] || i}
                className={`cm-day cm-day-${lvl}`}
                title={`${fmtDay(shownLabels[i] || '')}: ${fmtH(v, 2)} h`}
              >
                {dense ? '' : (v > 0 ? fmtH(v) : '·')}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      key: 'week',
      header: `Week h / ${weekTarget}`,
      className: 'cm-col-num',
      render: (d) => (
        <span className={d.week_hours >= weekTarget ? 'cm-good' : 'cm-bad'}>
          {fmtH(d.week_hours)} <span className="cm-muted">/ {weekTarget}</span>
        </span>
      ),
    },
    { key: 'pace', header: 'h/day', className: 'cm-col-num', render: (d) => fmtH(d.pace, 2) },
    { key: 'prev', header: 'Prior 7d', className: 'cm-col-num', render: (d) => fmtH(d.prev_week_hours) },
    { key: 'last', header: 'Last recording', render: (d) => fmtStamp(d.last_recording) },
    {
      key: 'silent',
      header: 'Silent',
      className: 'cm-col-num',
      render: (d) => (
        <span className={d.status === 'not_recording' || d.status === 'never' ? 'cm-bad' : ''}>
          {silentLabel(d.silent, d.last_recording)}
        </span>
      ),
    },
    { key: 'total', header: 'Total h', className: 'cm-col-num', render: (d) => fmtH(d.total_hours) },
    {
      key: 'status',
      header: 'Status',
      render: (d) => <span className={`cm-badge cm-badge-${d.status}`}>{STATUS_LABEL[d.status]}</span>,
    },
  ];

  return (
    <div className="cm-overlay" onClick={onClose}>
      <aside className="cm-drawer" onClick={(e) => e.stopPropagation()} aria-label={`${org} details`}>
        <header className="cm-drawer-head">
          <div>
            <h2>{label || org}</h2>
            {label && label !== org && <div className="cm-muted">Org ID {org}</div>}
            {orgRow && <span className={`cm-badge cm-badge-${orgRow.status}`}>{STATUS_LABEL[orgRow.status]}</span>}
          </div>
          <button className="cm-close" onClick={onClose} aria-label="Close">&times;</button>
        </header>

        {orgRow && (
          <div className="cm-summary cm-summary-compact">
            <div className="cm-stat">
              <div className="cm-stat-label">Last 7 days</div>
              <div className="cm-stat-value">{fmtH(orgRow.week_hours)} <span className="cm-stat-unit">/ {fmtH(orgRow.week_target_hours, 0)} h</span></div>
              <div className="cm-stat-sub">prior 7 days {fmtH(orgRow.prev_week_hours)} h</div>
            </div>
            <div className="cm-stat">
              <div className="cm-stat-label">h/day/headset</div>
              <div className={`cm-stat-value ${orgRow.pace >= paceTarget ? 'cm-good' : 'cm-bad'}`}>
                {fmtH(orgRow.pace, 2)} <span className="cm-stat-unit">/ {paceTarget}</span>
              </div>
              <div className="cm-stat-sub">{orgRow.at_pace} of {orgRow.headset_count} headsets at target</div>
            </div>
            <div className="cm-stat">
              <div className="cm-stat-label">Last recording</div>
              <div className="cm-stat-value cm-stat-small">{fmtStamp(orgRow.last_recording)}</div>
              <div className="cm-stat-sub">{!orgRow.last_recording ? 'No recordings yet' : orgRow.silent_days === 0 ? 'Recorded today' : `${silentLabel(orgRow.silent_days, orgRow.last_recording)} ago`} · {fmtH(orgRow.total_hours, 0)} h all time</div>
            </div>
          </div>
        )}

        <section className="cm-drawer-section">
          <div className="cm-section-head">
            <h3>Headsets ({rows.length})</h3>
            <div className="cm-range">
              {RANGES.filter((r) => r <= maxRange).map((r) => (
                <button key={r} className={r === range ? 'cm-range-on' : ''} onClick={() => setRange(r)}>
                  {r}d
                </button>
              ))}
            </div>
          </div>
          <DataTable
            className="cm-table cm-headset-table"
            columns={columns}
            data={rows}
            rowKey={(d) => d.id}
            rowClassName={(d) => (d.status === 'not_recording' || d.status === 'never' ? 'cm-row-silent' : undefined)}
            emptyMessage="No E2 headsets for this customer."
          />
          <div className="cm-legend cm-muted">
            <span className="cm-day cm-day-0" /> none
            <span className="cm-day cm-day-1" /> under {paceTarget / 2} h
            <span className="cm-day cm-day-2" /> {paceTarget / 2}–{paceTarget} h
            <span className="cm-day cm-day-3" /> {paceTarget}+ h (daily target)
          </div>
        </section>

        <section className="cm-drawer-section">
          <h3>Weekly progress email</h3>
          {email?.body_cameras !== 0 && email?.body_cameras !== undefined && (
            <Alert type="info">
              {email.body_cameras == null
                ? "Couldn't check whether this customer runs body cameras, so they get no customer email until the next data refresh."
                : `This customer also runs ${email.body_cameras} body camera${email.body_cameras === 1 ? '' : 's'}, so they never get the customer email. The team weekly email still includes their preview.`}
            </Alert>
          )}
          {!globalEnabled && (
            <Alert type="info">Customer emails are off, so this customer gets nothing yet. The team weekly email still includes a preview of what they would get. Settings saved here apply once customer emails are on.</Alert>
          )}
          <div className="cm-form-row">
            <label className="cm-inline-field">
              This customer
              <select value={mode} onChange={(e) => setMode(e.target.value as EmailMode)}>
                <option value="inherit">Inherit (currently {defaultOn ? 'on' : 'off'})</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </label>
            <span className={resolvedOn && globalEnabled && recipients.length ? 'cm-good' : 'cm-muted'}>
              {email?.body_cameras !== 0 && email?.body_cameras !== undefined ? 'Will not send (has body cameras)'
                : !globalEnabled ? 'Will not send (global off)'
                : !resolvedOn ? 'Will not send (off for this customer)'
                  : recipients.length ? `Will send to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`
                    : 'Will not send (no recipients)'}
            </span>
          </div>
          <label className="cm-inline-field">
            Name in emails
            <input
              type="text"
              value={displayName}
              maxLength={80}
              onChange={(e) => setDisplayName(e.target.value)}
              aria-label="Customer name as it appears in emails"
            />
          </label>
          <label className="cm-block-field">
            Recipients (comma or newline separated)
            <textarea
              rows={2}
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              placeholder="ops@customer.com, manager@customer.com"
            />
          </label>
          <div className="cm-suggestions">
            <span className="cm-muted">Suggested:</span>
            {suggestions === null && <LoadingSpinner inline size="sm" message="" />}
            {suggestions && suggestions.length === 0 && <span className="cm-muted">no known contacts for this customer</span>}
            {suggestions && suggestions.map((s) => (
              <button
                key={s.email}
                className="cm-chip"
                onClick={() => addSuggestion(s.email)}
                title={`${s.source}${s.name ? ` · ${s.name}` : ''}`}
                disabled={recipients.some((r) => r.toLowerCase() === s.email.toLowerCase())}
              >
                + {s.email} <span className="cm-muted">({s.source})</span>
              </button>
            ))}
          </div>
          <div className="cm-form-row">
            <Button size="sm" onClick={save} loading={saving} disabled={!dirty}>Save email settings</Button>
            <span className="cm-muted">
              Last sent:{' '}
              {email?.last_sent
                ? `${email.last_sent.status} ${fmtWhen(email.last_sent.at)} (week of ${fmtStamp(email.last_sent.week)})`
                : 'never'}
            </span>
          </div>

          <div className="cm-form-row cm-preview-row">
            <label className="cm-inline-field">
              Email
              <select value={previewKind} onChange={(e) => setPreviewKind(e.target.value as 'summary' | 'reminder')}>
                <option value="summary">Weekly summary</option>
                <option value="reminder">Reminder to record more (preview only)</option>
              </select>
            </label>
            <label className="cm-inline-field">
              week of
              <select value={previewWeek} onChange={(e) => setPreviewWeek(e.target.value)}>
                {weekOptions.map((w, i) => (
                  <option key={w} value={w}>{fmtStamp(w)}{i === 0 ? ' (next email)' : ''}</option>
                ))}
              </select>
            </label>
            <Button size="sm" variant="outline" loading={previewLoading} onClick={() => loadPreview(previewWeek)}>
              Preview email
            </Button>
          </div>

          <div className="cm-form-row">
            <label className="cm-inline-field">
              Send a test to
              <input
                type="email"
                value={testTo}
                onChange={(e) => { setTestTo(e.target.value); setTestConfirm(false); }}
                placeholder="you@plix.ai"
              />
            </label>
            {!testConfirm ? (
              <Button size="sm" variant="secondary" disabled={!/^\S+@\S+\.\S+$/.test(testTo.trim())} onClick={() => setTestConfirm(true)}>
                Send test…
              </Button>
            ) : (
              <>
                <span>Send the {fmtStamp(previewWeek)} {previewKind === 'reminder' ? 'reminder' : 'summary'} for {org} to {testTo.trim()} only?</span>
                <Button size="sm" loading={testSending} onClick={sendTest}>Send</Button>
                <Button size="sm" variant="ghost" onClick={() => setTestConfirm(false)}>Cancel</Button>
              </>
            )}
          </div>
          <Alert type="success" onDismiss={() => setLocalMsg(null)}>{localMsg}</Alert>
        </section>
      </aside>

      {preview && (
        <div className="cm-modal" onClick={(e) => { e.stopPropagation(); setPreview(null); }}>
          <div className="cm-modal-body" onClick={(e) => e.stopPropagation()}>
            <header className="cm-drawer-head">
              <div>
                <div className="cm-muted">Subject</div>
                <strong>{preview.subject}</strong>
                {preview.kind === 'reminder' && preview.reminder_needed === false && (
                  <div className="cm-muted">This customer met the goal that week, so the team batch would not include a reminder.</div>
                )}
                {preview.partial && (
                  <div className="cm-muted">This week is not over yet; numbers will grow until it ends.</div>
                )}
              </div>
              <button className="cm-close" onClick={() => setPreview(null)} aria-label="Close preview">&times;</button>
            </header>
            <iframe title={`Email preview for ${org}`} className="cm-preview-frame" srcDoc={preview.html} sandbox="" />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerOrgDetail;
